import React, { useState } from "react";
import { PDFDocument, rgb, StandardFonts } from "pdf-lib";
import axios from "axios";
import config from "../../config";
import JsBarcode from "jsbarcode";
// Helper to fetch image URL and convert to Uint8Array for embedding into PDF
async function fetchImageAsUint8Array(url) {
  if (!url) return null;
  try {
    const response = await fetch(url);
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    const blob = await response.blob();
    return new Uint8Array(await blob.arrayBuffer());
  } catch (error) {
    console.warn("Failed to fetch image:", error);
    return null;
  }
}

const generateBarcodeImage = async (value) => {
  try {
    console.log("Generating barcode for:", value);

    // Create a canvas element with dimensions matching the reference more closely
    const canvas = document.createElement("canvas");
    canvas.width = 250; // Increased width for better fit
    canvas.height = 60; // Increased height to accommodate text

    JsBarcode(canvas, value, {
      format: "CODE128",
      width: 1.5, // Thinner bars to fit more bars in the space
      height: 35, // Taller bars to match reference better
      displayValue: true, // Show the text below barcode
      background: "#ffffff",
      lineColor: "#000000",
      margin: 5, // Reduced margin
      fontSize: 10, // Font size for the text below
      textMargin: 2, // Space between barcode and text
      font: "monospace", // Use monospace font
    });

    console.log("JsBarcode generation completed");

    // Convert canvas to blob then to Uint8Array
    return new Promise((resolve) => {
      canvas.toBlob(async (blob) => {
        if (blob) {
          console.log("Canvas converted to blob successfully");
          const arrayBuffer = await blob.arrayBuffer();
          resolve(new Uint8Array(arrayBuffer));
        } else {
          console.error("Failed to convert canvas to blob");
          resolve(null);
        }
      }, "image/png");
    });
  } catch (error) {
    console.error("Error generating barcode:", error);
    return null;
  }
};

// Simple text wrapper for pdf-lib
function wrapText(text, font, fontSize, maxWidth) {
  const words = text.split(" ");
  let lines = [];
  let currentLine = "";

  for (let word of words) {
    const testLine = currentLine ? currentLine + " " + word : word;
    const testWidth = font.widthOfTextAtSize(testLine, fontSize);

    if (testWidth <= maxWidth) {
      currentLine = testLine;
    } else {
      if (currentLine) lines.push(currentLine);
      currentLine = word;
    }
  }
  if (currentLine) lines.push(currentLine);
  return lines;
}

const ChecklistPdfButton = ({ selectedCandidates }) => {
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [loadingText, setLoadingText] = useState("");

  const [showPdfViewer, setShowPdfViewer] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const closePdfViewer = () => {
    setShowPdfViewer(false);

    if (pdfUrl) {
      URL.revokeObjectURL(pdfUrl);
      setPdfUrl(null);
    }
    window.location.reload();
  };

  const handleGeneratePdf = async () => {
    if (!selectedCandidates.length) {
      alert("Please select at least one candidate.");
      return;
    }

    try {
      setLoading(true);
      setProgress(0);
      setLoadingText("Initializing...");

      const token = sessionStorage.getItem("tokenclassix");
      const candidateIdString = selectedCandidates.join(",");

      // Step 1: Fetch checklist data
      setProgress(10);
      setLoadingText("Fetching student data...");

      const response = await axios.post(
        `${config.API_BASE_URL}/CheckListGeneratedByCandidateID_CL9`,
        {
          candidatE_ID: candidateIdString,
          districtcode: sessionStorage.getItem("district"),
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      const students = response.data.data?.checklist_data || [];
      if (!students.length) throw new Error("No data found.");

      setProgress(20);
      setLoadingText("Creating PDF document...");

      // Create PDF document
      const pdfDoc = await PDFDocument.create();
      const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
      const bold = await pdfDoc.embedFont(StandardFonts.HelveticaBold);

      setProgress(30);
      setLoadingText("Setting up document layout...");

      // Constants for page layout
      const PAGE_WIDTH = 595;
      const PAGE_HEIGHT = 842;
      const MARGIN = 20;
      const TABLE_WIDTH = PAGE_WIDTH - 2 * MARGIN;

      const totalStudents = students.length;

      for (
        let studentIndex = 0;
        studentIndex < students.length;
        studentIndex++
      ) {
        const student = students[studentIndex];

        // Update progress for each student
        const studentProgress = 30 + (studentIndex / totalStudents) * 60;
        setProgress(Math.round(studentProgress));
        setLoadingText(
          `Processing student ${studentIndex + 1} of ${totalStudents}...`
        );

        const page = pdfDoc.addPage([PAGE_WIDTH, PAGE_HEIGHT]);
        let y = PAGE_HEIGHT - MARGIN;

        // **EMBED LOGO/SEAL - positioned like your reference**
        /* try {
          const logoUrl = "../assets/images/jac-academic-council.png";
          const logoBytes = await fetchImageAsUint8Array(logoUrl);
          if (logoBytes) {
            let logoImage;
            try {
              logoImage = await pdfDoc.embedPng(logoBytes);
            } catch {
              logoImage = await pdfDoc.embedJpg(logoBytes);
            }

            // Position logo on the left side
            page.drawImage(logoImage, {
              x: MARGIN + 5,
              y: y - 45,
              width: 40,
              height: 40,
            });
          }
        } catch (error) {
          console.warn("Failed to embed logo:", error);
        } */

        // **HEADER SECTION - Fixed to avoid logo overlap**
        const headerStartY = y - 13;

        // School Name - positioned completely above the logo and table area
        const schoolNameText = `${student.schooL_NAME || ""}`;
        const schoolNameWidth = bold.widthOfTextAtSize(schoolNameText, 11);
        const schoolNameX = (PAGE_WIDTH - schoolNameWidth) / 2;

        page.drawText(schoolNameText, {
          x: schoolNameX,
          y: headerStartY + 10, // Moved higher up
          size: 11,
          font: bold,
          color: rgb(0, 0, 0),
        });

        // School Code - centered below school name
        const schoolCodeText = `School Code : ${student.schooL_CODE || ""}`;
        const schoolCodeWidth = font.widthOfTextAtSize(schoolCodeText, 10);
        const schoolCodeX = (PAGE_WIDTH - schoolCodeWidth) / 2;

        page.drawText(schoolCodeText, {
          x: schoolCodeX,
          y: headerStartY - 5,
          size: 10,
          font,
          color: rgb(0, 0, 0),
        });

        // Class IX Student Information Sheet - centered below school code
        const titleText = "Class IX Student Information Sheet";
        const titleWidth = bold.widthOfTextAtSize(titleText, 10);
        const titleX = (PAGE_WIDTH - titleWidth) / 2;

        page.drawText(titleText, {
          x: titleX,
          y: headerStartY - 20,
          size: 10,
          font: bold,
          color: rgb(0, 0, 0),
        });

        // Update y position after header
        y = headerStartY - 35;

        // **SINGLE COMPREHENSIVE TABLE WITH PROPER BARCODE**
        const drawSingleComprehensiveTable = async (startY) => {
          let currentY = startY;
          const rowHeight = 18; // Slightly taller rows like your reference
          const barcodeRowHeight = 35;
          const imageContainerHeight = 70;

          const studentIdRows = 1;
          const studentDetailsRows = 5 + 1;
          const addressRows = 4 + 1;
          const educationalRows = 7 + 1;
          const imageHeaderRows = 2;
          const imageContainerRows = Math.ceil(
            imageContainerHeight / rowHeight
          );
          const documentsRow = 1;
          const submissionRows = 3 + 1;

          const totalDataRows =
            studentIdRows +
            studentDetailsRows +
            addressRows +
            educationalRows +
            imageHeaderRows +
            imageContainerRows +
            documentsRow +
            submissionRows;
          const totalHeight =
            totalDataRows * rowHeight +
            (imageContainerHeight - imageContainerRows * rowHeight);

          // Draw main table border
          page.drawRectangle({
            x: MARGIN,
            y: currentY - totalHeight,
            width: TABLE_WIDTH,
            height: totalHeight,
            color: rgb(1, 1, 1),
            borderColor: rgb(0, 0, 0),
            borderWidth: 1,
          });

          // **STUDENT ID SECTION WITH PROPER BARCODE - MATCHING YOUR REFERENCE**
          const col1Width = 140;
          const col2Width = 140;
          const col3Width = TABLE_WIDTH - col1Width - col2Width;

          // Student ID label cell
          page.drawRectangle({
            x: MARGIN,
            y: currentY - barcodeRowHeight,
            width: col1Width,
            height: barcodeRowHeight,
            color: rgb(0.95, 0.95, 0.95), // Light gray like your reference
            borderColor: rgb(0, 0, 0),
            borderWidth: 0.5,
          });
          page.drawText("Student ID -", {
            x: MARGIN + 5,
            y: currentY - barcodeRowHeight / 2,
            size: 9,
            font: bold,
            color: rgb(0, 0, 0),
          });

          // Student ID value cell
          page.drawRectangle({
            x: MARGIN + col1Width,
            y: currentY - barcodeRowHeight,
            width: col2Width,
            height: barcodeRowHeight,
            color: rgb(1, 1, 1),
            borderColor: rgb(0, 0, 0),
            borderWidth: 0.5,
          });
          page.drawText(student.candidatE_ID || "", {
            x: MARGIN + col1Width + 5,
            y: currentY - barcodeRowHeight / 2,
            size: 9,
            font,
            color: rgb(0, 0, 0),
          });

          // **BARCODE CELL - IMPROVED VERSION**
          page.drawRectangle({
            x: MARGIN + col1Width + col2Width,
            y: currentY - barcodeRowHeight,
            width: col3Width,
            height: barcodeRowHeight,
            color: rgb(1, 1, 1),
            borderColor: rgb(0, 0, 0),
            borderWidth: 0.5,
          });

          if (student.candidatE_ID) {
            try {
              console.log(
                "Attempting to generate barcode for:",
                student.candidatE_ID
              );
              const barcodeBytes = await generateBarcodeImage(
                student.candidatE_ID
              );

              if (barcodeBytes) {
                console.log("Barcode generated successfully, embedding in PDF");
                const barcodeImage = await pdfDoc.embedPng(barcodeBytes);

                // Calculate proper dimensions to fill the cell better
                const cellWidth = col3Width - 10; // Leave 5px margin on each side
                const cellHeight = barcodeRowHeight - 6; // Leave 3px margin top/bottom

                // Calculate aspect ratio and fit the barcode properly
                const barcodeAspectRatio = 250 / 60; // canvas width/height ratio
                let barcodeWidth = cellWidth;
                let barcodeHeight = barcodeWidth / barcodeAspectRatio;

                // If height is too tall, scale down based on height
                if (barcodeHeight > cellHeight) {
                  barcodeHeight = cellHeight;
                  barcodeWidth = barcodeHeight * barcodeAspectRatio;
                }

                // Center the barcode in the cell
                const barcodeX =
                  MARGIN +
                  col1Width +
                  col2Width +
                  (col3Width - barcodeWidth) / 2;
                const barcodeY =
                  currentY - (barcodeRowHeight + barcodeHeight) / 2;

                page.drawImage(barcodeImage, {
                  x: barcodeX,
                  y: barcodeY,
                  width: barcodeWidth,
                  height: barcodeHeight,
                });

                console.log("Barcode embedded successfully in PDF");
              } else {
                console.error("Barcode generation returned null");
                // Fallback: Simple text representation
                page.drawText(`[${student.candidatE_ID}]`, {
                  x: MARGIN + col1Width + col2Width + 10,
                  y: currentY - 20,
                  size: 10,
                  font: bold,
                  color: rgb(0, 0, 0),
                });
              }
            } catch (error) {
              console.error("Failed to generate/embed barcode:", error);
              // Fallback: Display student ID as text
              page.drawText(`ID: ${student.candidatE_ID}`, {
                x: MARGIN + col1Width + col2Width + 10,
                y: currentY - 20,
                size: 10,
                font: bold,
                color: rgb(0, 0, 0),
              });
            }
          }

          currentY -= barcodeRowHeight;

          // **STUDENT DETAILS HEADER**
          page.drawRectangle({
            x: MARGIN,
            y: currentY - rowHeight,
            width: TABLE_WIDTH,
            height: rowHeight,
            color: rgb(0.85, 0.85, 0.85),
            borderColor: rgb(0, 0, 0),
            borderWidth: 0.5,
          });
          page.drawText("Student Details", {
            x: MARGIN + 5,
            y: currentY - 12,
            size: 10,
            font: bold,
            color: rgb(0, 0, 0),
          });
          currentY -= rowHeight;

          // --- Student Details Rows ---
          const studentDetailsData = [
            [
              "Candidate Name",
              student.candidatE_NAME || "",
              "Father's Name",
              student.fatheR_NAME || "",
            ],
            [
              "Mother's Name",
              student.motheR_NAME || "",
              "Gender",
              student.gender || "",
            ],
            [
              "Date of Birth",
              student.dob || "",
              "Religion",
              student.religioN_NAME || "",
            ],
            [
              "Nationality",
              student.nationality || "",
              "Aadhaar No.",
              student.aadhaR_CARD || "",
            ],
            [
              "Caste Category",
              student.castE_NAME || "",
              "Divyang Category",
              student.divyanG_NAME || "",
            ],
          ];

          for (const row of studentDetailsData) {
            const colWidth = TABLE_WIDTH / 4;

            // find required row height based on wrapped text
            let rowMaxHeight = rowHeight;
            const wrappedCells = [];

            for (let i = 0; i < 4; i++) {
              const cellData = row[i] || "";
              const isLabel = i % 2 === 0;
              const fontToUse = isLabel ? bold : font;

              const lines = wrapText(cellData, fontToUse, 9, colWidth - 10);
              wrappedCells.push({ lines, isLabel, font: fontToUse });

              const textHeight = lines.length * 11 + 4;
              if (textHeight > rowMaxHeight) {
                rowMaxHeight = textHeight;
              }
            }

            // draw cells
            for (let i = 0; i < 4; i++) {
              const { lines, isLabel, font: fontToUse } = wrappedCells[i];

              // cell box
              page.drawRectangle({
                x: MARGIN + i * colWidth,
                y: currentY - rowMaxHeight,
                width: colWidth,
                height: rowMaxHeight,
                color: isLabel ? rgb(0.97, 0.97, 0.97) : rgb(1, 1, 1),
                borderColor: rgb(0, 0, 0),
                borderWidth: 0.5,
              });

              // text (multi-line)
              lines.forEach((line, lineIndex) => {
                page.drawText(line, {
                  x: MARGIN + i * colWidth + 5,
                  y: currentY - 12 - lineIndex * 11,
                  size: 9,
                  font: fontToUse,
                  color: rgb(0, 0, 0),
                });
              });
            }

            currentY -= rowMaxHeight;
          }
          // **ADDRESS HEADER**
          page.drawRectangle({
            x: MARGIN,
            y: currentY - rowHeight,
            width: TABLE_WIDTH,
            height: rowHeight,
            color: rgb(0.85, 0.85, 0.85), // Darker gray to match reference
            borderColor: rgb(0, 0, 0),
            borderWidth: 0.5,
          });
          page.drawText("Communication Details", {
            x: MARGIN + 5,
            y: currentY - 12,
            size: 10,
            font: bold,
            color: rgb(0, 0, 0),
          });
          currentY -= rowHeight;

          // **ADDRESS ROWS** - Fixed layout to match reference exactly
          const addressData = [
            ["Area", student.area || "", "Block", student.blocK_NAME || ""],
            [
              "District",
              student.district || "",
              "State",
              student.statE_NAME || "",
            ],
            ["Address", student.address || "", "", ""],
            [
              "Contact Number",
              student.contacT_NO || "",
              "Pin Code",
              student.pincode || "",
            ],
            ["Email ID", student.emaiL_ID || "", "", ""], // Contact spans 2 columns like in reference
          ];

          for (let rowIndex = 0; rowIndex < addressData.length; rowIndex++) {
            const row = addressData[rowIndex];

            if (rowIndex === 0) {
              // Row 0: Area / Block (wrap Block)
              const colWidth = TABLE_WIDTH / 4;
              const blockText = student.blocK_NAME || "";
              const availableWidth = colWidth - 10;
              const lines = wrapText(blockText, font, 9, availableWidth);
              const lineHeight = 11;
              const totalHeight = Math.max(
                lines.length * lineHeight + 4,
                rowHeight
              );

              for (let i = 0; i < 4; i++) {
                const isLabel = i % 2 === 0;
                const cellData = row[i] || "";

                page.drawRectangle({
                  x: MARGIN + i * colWidth,
                  y: currentY - totalHeight,
                  width: colWidth,
                  height: totalHeight,
                  color: isLabel ? rgb(0.97, 0.97, 0.97) : rgb(1, 1, 1),
                  borderColor: rgb(0, 0, 0),
                  borderWidth: 0.5,
                });

                if (cellData) {
                  if (i === 3) {
                    // Wrap Block value
                    lines.forEach((line, idx) => {
                      if (line.trim() !== "") {
                        page.drawText(line, {
                          x: MARGIN + i * colWidth + 5,
                          y: currentY - 12 - idx * lineHeight,
                          size: 9,
                          font,
                          color: rgb(0, 0, 0),
                        });
                      }
                    });
                  } else {
                    // Normal for Area label/value
                    page.drawText(cellData, {
                      x: MARGIN + i * colWidth + 5,
                      y: currentY - 12,
                      size: 9,
                      font: isLabel ? bold : font,
                      color: rgb(0, 0, 0),
                    });
                  }
                }
              }

              currentY -= totalHeight;
            } else if (rowIndex === 1) {
              // Row 1: District / State (wrap State)
              const colWidth = TABLE_WIDTH / 4;
              const stateText = student.statE_NAME || "";
              const availableWidth = colWidth - 10;
              const lines = wrapText(stateText, font, 9, availableWidth);
              const lineHeight = 11;
              const totalHeight = Math.max(
                lines.length * lineHeight + 4,
                rowHeight
              );

              for (let i = 0; i < 4; i++) {
                const isLabel = i % 2 === 0;
                const cellData = row[i] || "";

                page.drawRectangle({
                  x: MARGIN + i * colWidth,
                  y: currentY - totalHeight,
                  width: colWidth,
                  height: totalHeight,
                  color: isLabel ? rgb(0.97, 0.97, 0.97) : rgb(1, 1, 1),
                  borderColor: rgb(0, 0, 0),
                  borderWidth: 0.5,
                });

                if (cellData) {
                  if (i === 3) {
                    // Wrap State value
                    lines.forEach((line, idx) => {
                      if (line.trim() !== "") {
                        page.drawText(line, {
                          x: MARGIN + i * colWidth + 5,
                          y: currentY - 12 - idx * lineHeight,
                          size: 9,
                          font,
                          color: rgb(0, 0, 0),
                        });
                      }
                    });
                  } else {
                    // Normal for other cells
                    page.drawText(cellData, {
                      x: MARGIN + i * colWidth + 5,
                      y: currentY - 12,
                      size: 9,
                      font: isLabel ? bold : font,
                      color: rgb(0, 0, 0),
                    });
                  }
                }
              }

              currentY -= totalHeight;
            } else if (rowIndex === 2) {
              // Row 2: Address (full-width wrap)
              const labelWidth = TABLE_WIDTH / 4;
              const valueWidth = TABLE_WIDTH - labelWidth;

              const addressText = student.address || "";
              const availableWidth = valueWidth - 10;
              const lines = wrapText(addressText, font, 9, availableWidth);
              const lineHeight = 11;
              const totalHeight = Math.max(
                lines.length * lineHeight + 4,
                rowHeight
              );

              // Address label
              page.drawRectangle({
                x: MARGIN,
                y: currentY - totalHeight,
                width: labelWidth,
                height: totalHeight,
                color: rgb(0.97, 0.97, 0.97),
                borderColor: rgb(0, 0, 0),
                borderWidth: 0.5,
              });
              page.drawText("Address", {
                x: MARGIN + 5,
                y: currentY - 12,
                size: 9,
                font: bold,
                color: rgb(0, 0, 0),
              });

              // Address value
              page.drawRectangle({
                x: MARGIN + labelWidth,
                y: currentY - totalHeight,
                width: valueWidth,
                height: totalHeight,
                color: rgb(1, 1, 1),
                borderColor: rgb(0, 0, 0),
                borderWidth: 0.5,
              });

              lines.forEach((line, idx) => {
                if (line.trim() !== "") {
                  page.drawText(line, {
                    x: MARGIN + labelWidth + 5,
                    y: currentY - 12 - idx * lineHeight,
                    size: 9,
                    font,
                    color: rgb(0, 0, 0),
                  });
                }
              });

              currentY -= totalHeight;
            } else if (rowIndex === 4) {
              // Row 4: Email (spans two columns)
              const labelWidth = TABLE_WIDTH / 4;
              const valueWidth = TABLE_WIDTH - labelWidth;

              page.drawRectangle({
                x: MARGIN,
                y: currentY - rowHeight,
                width: labelWidth,
                height: rowHeight,
                color: rgb(0.97, 0.97, 0.97),
                borderColor: rgb(0, 0, 0),
                borderWidth: 0.5,
              });
              page.drawText("Email ID", {
                x: MARGIN + 5,
                y: currentY - 12,
                size: 9,
                font: bold,
                color: rgb(0, 0, 0),
              });

              page.drawRectangle({
                x: MARGIN + labelWidth,
                y: currentY - rowHeight,
                width: valueWidth,
                height: rowHeight,
                color: rgb(1, 1, 1),
                borderColor: rgb(0, 0, 0),
                borderWidth: 0.5,
              });
              page.drawText(student.emaiL_ID || "", {
                x: MARGIN + labelWidth + 5,
                y: currentY - 12,
                size: 9,
                font,
                color: rgb(0, 0, 0),
              });

              currentY -= rowHeight;
            } else {
              // Other rows (e.g. Contact No / Pin)
              const colWidth = TABLE_WIDTH / 4;
              for (let i = 0; i < 4; i++) {
                const cellData = row[i] || "";
                const isLabel = i % 2 === 0;

                page.drawRectangle({
                  x: MARGIN + i * colWidth,
                  y: currentY - rowHeight,
                  width: colWidth,
                  height: rowHeight,
                  color: isLabel ? rgb(0.97, 0.97, 0.97) : rgb(1, 1, 1),
                  borderColor: rgb(0, 0, 0),
                  borderWidth: 0.5,
                });

                if (cellData) {
                  page.drawText(cellData, {
                    x: MARGIN + i * colWidth + 5,
                    y: currentY - 12,
                    size: 9,
                    font: isLabel ? bold : font,
                    color: rgb(0, 0, 0),
                  });
                }
              }
              currentY -= rowHeight;
            }
          }

          // **EDUCATIONAL DETAILS HEADER** - Updated color
          page.drawRectangle({
            x: MARGIN,
            y: currentY - rowHeight,
            width: TABLE_WIDTH,
            height: rowHeight,
            color: rgb(0.85, 0.85, 0.85),
            borderColor: rgb(0, 0, 0),
            borderWidth: 0.5,
          });
          page.drawText("Educational Details", {
            x: MARGIN + 5,
            y: currentY - 12,
            size: 10,
            font: bold,
            color: rgb(0, 0, 0),
          });
          currentY -= rowHeight;

          // Helper function to split text into wrapped lines
          function getWrappedLines(text, font, fontSize, maxWidth) {
            if (!text) return [""];
            const words = text.split(" ");
            const lines = [];
            let currentLine = "";

            for (const word of words) {
              const testLine = currentLine ? `${currentLine} ${word}` : word;
              const testWidth = font.widthOfTextAtSize(testLine, fontSize);
              if (testWidth < maxWidth) {
                currentLine = testLine;
              } else {
                lines.push(currentLine);
                currentLine = word;
              }
            }
            if (currentLine) lines.push(currentLine);
            return lines;
          }

          // **EDUCATIONAL DETAILS ROWS**
          const eduData = [
            [
              "Category Choice",
              student.prinT_CATEGORY || "",
              "Medium of Examination",
              student.mediuM_NAME || "",
            ],
            [
              "Language I Subject",
              student.subjecT_1 || "",
              "Language II Subject",
              student.subjecT_2 || "",
            ],
            [
              "Compulsory I Subject",
              student.subjecT_3 || "",
              "Compulsory II Subject",
              student.subjecT_4 || "",
            ],
            [
              "Compulsory III Subject",
              student.subjecT_5 || "",
              "Additional Subject",
              student.subjecT_6 || "",
            ],
            [
              "Registration Number",
              student.registratioN_NO || "",
              "Section",
              student.section || "",
            ],
            [
              "Registration Session",
              student.sessioN_FULL_NAME || "",
              "Class Roll No",
              student.clasS_ROLL_NO || "",
            ],
            [
              "Board",
              student.boarD_NAME || "",
              "APAAR ID",
              student.apaaR_ID || "",
            ],
            ["PEN No.", student.peN_NO || "", "", ""],
          ];

          // ✅ Hybrid row height: expands if text wraps
          const minEduRowHeight = 18;
          const lineHeight = 11;
          const fontSize = 9;

          for (const row of eduData) {
            const colWidth = TABLE_WIDTH / 4;

            // Determine required height based on wrapped lines
            const maxLines = Math.max(
              ...row.map((cell, i) => {
                const fontToUse = i % 2 === 0 ? bold : font;
                const wrapped = getWrappedLines(
                  cell || "",
                  fontToUse,
                  fontSize,
                  colWidth - 10
                );
                return wrapped.length;
              })
            );
            const rowHeight = Math.max(
              minEduRowHeight,
              maxLines * lineHeight + 4
            );

            // Draw cells + text
            for (let i = 0; i < 4; i++) {
              const cellData = row[i] || "";
              const isLabel = i % 2 === 0;
              const cellX = MARGIN + i * colWidth;

              // Draw cell
              page.drawRectangle({
                x: cellX,
                y: currentY - rowHeight,
                width: colWidth,
                height: rowHeight,
                color: isLabel ? rgb(0.97, 0.97, 0.97) : rgb(1, 1, 1),
                borderColor: rgb(0, 0, 0),
                borderWidth: 0.5,
              });

              // Draw wrapped text
              const fontToUse = isLabel ? bold : font;
              const wrappedLines = getWrappedLines(
                cellData,
                fontToUse,
                fontSize,
                colWidth - 10
              );
              const totalTextHeight = wrappedLines.length * lineHeight;
              let textY = currentY - 10 - (rowHeight - totalTextHeight) / 2;

              for (const line of wrappedLines) {
                page.drawText(line, {
                  x: cellX + 5,
                  y: textY,
                  size: fontSize,
                  font: fontToUse,
                  color: rgb(0, 0, 0),
                });
                textY -= lineHeight;
              }
            }

            // Move to next row
            currentY -= rowHeight;
          }

          // **IMAGES UPLOADED HEADER** - Updated color
          page.drawRectangle({
            x: MARGIN,
            y: currentY - rowHeight,
            width: TABLE_WIDTH,
            height: rowHeight,
            color: rgb(0.85, 0.85, 0.85), // Darker gray to match reference
            borderColor: rgb(0, 0, 0),
            borderWidth: 0.5,
          });
          page.drawText("Images Uploaded", {
            x: MARGIN + 5,
            y: currentY - 12,
            size: 10,
            font: bold,
            color: rgb(0, 0, 0),
          });
          currentY -= rowHeight;

          // **IMAGE COLUMN HEADERS ROW - PROPERLY STRUCTURED**
          const leftCellWidth = TABLE_WIDTH / 2;
          const rightCellWidth = TABLE_WIDTH / 2;

          // Left header cell (Photograph)
          page.drawRectangle({
            x: MARGIN,
            y: currentY - rowHeight,
            width: leftCellWidth,
            height: rowHeight,
            color: rgb(0.97, 0.97, 0.97),
            borderColor: rgb(0, 0, 0),
            borderWidth: 0.5,
          });

          // Center "Photograph" text in left cell
          const photographText = "Photograph";
          const photographWidth = font.widthOfTextAtSize(photographText, 9);
          const photographX = MARGIN + (leftCellWidth - photographWidth) / 2;
          page.drawText(photographText, {
            x: photographX,
            y: currentY - 12,
            size: 9,
            font: bold,
            color: rgb(0, 0, 0),
          });

          // Right header cell (Signature)
          page.drawRectangle({
            x: MARGIN + leftCellWidth,
            y: currentY - rowHeight,
            width: rightCellWidth,
            height: rowHeight,
            color: rgb(0.97, 0.97, 0.97),
            borderColor: rgb(0, 0, 0),
            borderWidth: 0.5,
          });

          // Center "Signature" text in right cell
          const signatureText = "Signature";
          const signatureWidth = font.widthOfTextAtSize(signatureText, 9);
          const signatureX =
            MARGIN + leftCellWidth + (rightCellWidth - signatureWidth) / 2;
          page.drawText(signatureText, {
            x: signatureX,
            y: currentY - 12,
            size: 9,
            font: bold,
            color: rgb(0, 0, 0),
          });

          currentY -= rowHeight;

          // **IMAGE CONTAINER ROW**
          // Left image cell
          page.drawRectangle({
            x: MARGIN,
            y: currentY - imageContainerHeight,
            width: leftCellWidth,
            height: imageContainerHeight,
            color: rgb(1, 1, 1),
            borderColor: rgb(0, 0, 0),
            borderWidth: 0.5,
          });

          // Right image cell
          page.drawRectangle({
            x: MARGIN + leftCellWidth,
            y: currentY - imageContainerHeight,
            width: rightCellWidth,
            height: imageContainerHeight,
            color: rgb(1, 1, 1),
            borderColor: rgb(0, 0, 0),
            borderWidth: 0.5,
          });

          // **EMBED IMAGES - CENTERED IN THEIR CELLS**
          if (student.studenT_PHOTOGRAPH_URL) {
            const photoUrl = student.studenT_PHOTOGRAPH_URL.startsWith("http")
              ? student.studenT_PHOTOGRAPH_URL.replace("http://", "https://")
              : student.studenT_PHOTOGRAPH_URL;
            const photoBytes = await fetchImageAsUint8Array(
              student.studenT_PHOTOGRAPH_URL
            );
            if (photoBytes) {
              try {
                let photoImage;
                try {
                  photoImage = await pdfDoc.embedJpg(photoBytes);
                } catch {
                  photoImage = await pdfDoc.embedPng(photoBytes);
                }

                // Center the photograph in the left cell
                const photoWidth = 50;
                const photoHeight = 50;
                const photoCellCenterX = MARGIN + leftCellWidth / 2;
                const photoCellCenterY = currentY - imageContainerHeight / 2;

                page.drawImage(photoImage, {
                  x: photoCellCenterX - photoWidth / 2,
                  y: photoCellCenterY - photoHeight / 2,
                  width: photoWidth,
                  height: photoHeight,
                });
              } catch (error) {
                console.warn("Failed to embed photograph:", error);
              }
            }
          }

          if (student.studenT_SIGNATURE_URL) {
            const signUrl = student.studenT_SIGNATURE_URL.startsWith("http")
              ? student.studenT_SIGNATURE_URL.replace("http://", "https://")
              : student.studenT_SIGNATURE_URL;
            // const signBytes = base64ToUint8Array(student.studenT_SIGNATURE_URL);

            const signBytes = await fetchImageAsUint8Array(signUrl);
            if (signBytes) {
              try {
                let signImage;
                try {
                  signImage = await pdfDoc.embedJpg(signBytes);
                } catch {
                  signImage = await pdfDoc.embedPng(signBytes);
                }

                // Center the signature in the right cell
                const signWidth = 70;
                const signHeight = 25;
                const signCellCenterX =
                  MARGIN + leftCellWidth + rightCellWidth / 2;
                const signCellCenterY = currentY - imageContainerHeight / 2;

                page.drawImage(signImage, {
                  x: signCellCenterX - signWidth / 2,
                  y: signCellCenterY - signHeight / 2,
                  width: signWidth,
                  height: signHeight,
                });
              } catch (error) {
                console.warn("Failed to embed signature:", error);
              }
            }
          }

          currentY -= imageContainerHeight;

          // **DOCUMENTS UPLOADED ROW**
          const docsUploaded = [
            "Photograph",
            "Signature",
            student.castE_CERTIFICATE ? "Caste Certificate" : null,
            student.divyanG_CERTIFICATE ? "Divyang Certificate" : null,
          ]
            .filter(Boolean)
            .join(",");

          page.drawRectangle({
            x: MARGIN,
            y: currentY - rowHeight,
            width: TABLE_WIDTH,
            height: rowHeight,
            color: rgb(1, 1, 1),
            borderColor: rgb(0, 0, 0),
            borderWidth: 0.5,
          });
          page.drawText(`List of Documents Uploaded : ${docsUploaded}`, {
            x: MARGIN + 5,
            y: currentY - 12,
            size: 9,
            font,
            color: rgb(0, 0, 0),
          });
          currentY -= rowHeight;
          // **SUBMISSION DETAILS HEADER**
          page.drawRectangle({
            x: MARGIN,
            y: currentY - rowHeight,
            width: TABLE_WIDTH,
            height: rowHeight,
            color: rgb(0.85, 0.85, 0.85),
            borderColor: rgb(0, 0, 0),
            borderWidth: 0.5,
          });
          page.drawText("Submission Details", {
            x: MARGIN + 5,
            y: currentY - 12,
            size: 10,
            font: bold,
            color: rgb(0, 0, 0),
          });
          currentY -= rowHeight;

          // **SUBMISSION DETAILS ROWS**
          const submissionData = [
            [
              "Form Submission Date & Time",
              student.applY_SYS_DATE || "",
              "Last Edit Date & Time",
              student.lasT_UPDATE_DATE_TIME || "",
            ],
            [
              "DEO Approval Status",
              student.deO_STATUS || "",
              "DEO Approval Date & Time",
              student.statuS_CHANGE_DATETIME_APPROVED || "",
            ],
            [
              "Checklist Print Date & Time",
              student.checklisT_PRINT_DATE_TIME ||
                new Date().toLocaleString("en-IN", {
                  day: "2-digit",
                  month: "2-digit",
                  year: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                  second: "2-digit",
                  hour12: true,
                }),
              "",
              "",
            ],
          ];

          for (const row of submissionData) {
            const colWidth = TABLE_WIDTH / 4;
            for (let i = 0; i < 4; i++) {
              const cellData = row[i] || "";
              const isLabel = i % 2 === 0;

              page.drawRectangle({
                x: MARGIN + i * colWidth,
                y: currentY - rowHeight,
                width: colWidth,
                height: rowHeight,
                color: isLabel ? rgb(0.97, 0.97, 0.97) : rgb(1, 1, 1),
                borderColor: rgb(0, 0, 0),
                borderWidth: 0.5,
              });
              if (cellData) {
                page.drawText(cellData, {
                  x: MARGIN + i * colWidth + 5,
                  y: currentY - 12,
                  size: 9,
                  font: isLabel ? bold : font,
                  color: rgb(0, 0, 0),
                });
              }
            }
            currentY -= rowHeight;
          }

          return currentY;
        };

        y = await drawSingleComprehensiveTable(y);
        y -= 15;

        // **DECLARATION TEXT**
        const declarationText =
          "I hereby declare that I have diligently verified all the information presented in this document. I affirm that the content herein is true, authentic and accurate to the best of my knowledge.";
        const declLines = wrapText(declarationText, font, 9, TABLE_WIDTH);

        for (let i = 0; i < declLines.length; i++) {
          page.drawText(declLines[i], {
            x: MARGIN,
            y: y - i * 12,
            font,
            size: 9,
            color: rgb(0, 0, 0),
          });
        }
        y -= declLines.length * 12 + 20;

        const sigFieldWidth = TABLE_WIDTH / 4;
        const lineLength = sigFieldWidth - 20; // Leave margins on sides
        const lineY = y - 20; // Position of signature lines
        const textY = lineY - 15; // Position of text below lines

        const signatures = [
          "Student's Signature",
          "Parent's Signature",
          "Class Teacher's Signature",
          "Principal's Signature",
        ];

        for (let i = 0; i < 4; i++) {
          const fieldX = MARGIN + i * sigFieldWidth;
          const lineX = fieldX + 10; // Start line with 10px margin

          // Draw horizontal signature line
          page.drawLine({
            start: { x: lineX, y: lineY },
            end: { x: lineX + lineLength, y: lineY },
            thickness: 1,
            color: rgb(0, 0, 0),
          });

          // Draw centered text below line
          const label = signatures[i];
          const labelWidth = font.widthOfTextAtSize(label, 9);
          const labelX = lineX + (lineLength - labelWidth) / 2;

          page.drawText(label, {
            x: labelX,
            y: textY,
            size: 9,
            font,
            color: rgb(0, 0, 0),
          });
        }

        // Update y position after signature fields
        y = textY - 40;
      }

      setLoadingText("Finalizing PDF...");
      setProgress(90);

      // Create blob and display
      const pdfBytes = await pdfDoc.save();
      const blob = new Blob([pdfBytes], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);

      setLoadingText("PDF ready!");
      setProgress(100);

      // Small delay to show 100% completion
      setTimeout(() => {
        setPdfUrl(url);
        setShowPdfViewer(true);
        setLoading(false);
        setProgress(0);
      }, 500);
    } catch (error) {
      console.error(error);
      alert("Failed to generate checklist PDF.");
      setLoading(false);
      setProgress(0);
    }
  };
  const ProgressLoader = ({ progress, loadingText }) => {
    const handleClose = () => {
      window.location.reload();
    };

    return (
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          backgroundColor: "rgba(0, 0, 0, 0.8)",
          zIndex: 9999,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <div
          style={{
            backgroundColor: "white",
            borderRadius: "12px",
            padding: "40px",
            minWidth: "350px",
            textAlign: "center",
            boxShadow: "0 10px 30px rgba(0, 0, 0, 0.3)",
            position: "relative",
          }}
        >
          {/* ❌ Close button */}
          <button
            onClick={handleClose}
            style={{
              position: "absolute",
              top: "10px",
              right: "10px",
              background: "transparent",
              border: "none",
              fontSize: "24px",
              fontWeight: "bold",
              cursor: "pointer",
              color: "#999",
            }}
            aria-label="Close"
          >
            <i className="fa fa-times"></i>
          </button>

          {/* Circular Progress */}
          <div
            style={{
              position: "relative",
              width: "120px",
              height: "120px",
              margin: "0 auto 20px",
            }}
          >
            <svg
              width="120"
              height="120"
              viewBox="0 0 120 120"
              style={{ transform: "rotate(-90deg)" }}
            >
              <circle
                cx="60"
                cy="60"
                r="50"
                fill="none"
                stroke="#e0e0e0"
                strokeWidth="8"
              />
              <circle
                cx="60"
                cy="60"
                r="50"
                fill="none"
                stroke="#28a745"
                strokeWidth="8"
                strokeLinecap="round"
                strokeDasharray={`${2 * Math.PI * 50}`}
                strokeDashoffset={`${2 * Math.PI * 50 * (1 - progress / 100)}`}
                style={{ transition: "stroke-dashoffset 0.3s ease" }}
              />
            </svg>
            <div
              style={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                fontSize: "24px",
                fontWeight: "bold",
                color: "#28a745",
              }}
            >
              {progress}%
            </div>
          </div>

          {/* Progress bar */}
          <div
            style={{
              width: "100%",
              height: "8px",
              backgroundColor: "#e0e0e0",
              borderRadius: "4px",
              overflow: "hidden",
              marginBottom: "20px",
            }}
          >
            <div
              style={{
                width: `${progress}%`,
                height: "100%",
                backgroundColor: "#28a745",
                borderRadius: "4px",
                transition: "width 0.3s ease",
              }}
            />
          </div>

          {/* Loading text */}
          <div
            style={{
              fontSize: "16px",
              color: "#333",
              marginBottom: "10px",
              fontWeight: "500",
            }}
          >
            {loadingText}
          </div>

          {/* Generating PDF text */}
          <div
            style={{
              fontSize: "14px",
              color: "#666",
            }}
          >
            Generating PDF...
          </div>

          {/* Animated dots */}
          <div
            style={{
              fontSize: "20px",
              color: "#28a745",
              marginTop: "10px",
            }}
          >
            <span className="dots">...</span>
            <style>
              {`
              @keyframes dots {
                0%, 20% { content: ''; }
                40% { content: '.'; }
                60% { content: '..'; }
                80%, 100% { content: '...'; }
              }
            `}
            </style>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      {loading ? (
        <button className="btn btn-success btn-sm" disabled>
          Generating PDF... {progress}%
        </button>
      ) : (
        <button
          className="btn btn-success btn-sm"
          onClick={handleGeneratePdf}
          disabled={loading}
        >
          Print Checklist
        </button>
      )}

      {/* Progress Loader */}
      {loading && (
        <ProgressLoader progress={progress} loadingText={loadingText} />
      )}

      {/* PDF VIEWER MODAL WITH IFRAME - remains the same */}
      {showPdfViewer && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(0, 0, 0, 0.8)",
            zIndex: 9999,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          {/* PDF VIEWER MODAL WITH IFRAME */}
          {showPdfViewer && (
            <div
              style={{
                position: "fixed",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                backgroundColor: "rgba(0, 0, 0, 0.8)",
                zIndex: 9999,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <div
                style={{
                  width: "90%",
                  height: "90%",
                  backgroundColor: "white",
                  borderRadius: "8px",
                  position: "relative",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <div
                  style={{
                    padding: "10px",
                    borderBottom: "1px solid #ddd",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <h5 style={{ margin: 0 }}>Checklist PDF</h5>
                  <button
                    onClick={closePdfViewer}
                    style={{
                      background: "#dc3545",
                      color: "white",
                      border: "none",
                      borderRadius: "4px",
                      padding: "8px 16px",
                      cursor: "pointer",
                    }}
                  >
                    Close
                  </button>
                </div>

                <iframe
                  src={pdfUrl}
                  style={{
                    width: "100%",
                    height: "calc(100% - 60px)",
                    border: "none",
                    borderRadius: "0 0 8px 8px",
                  }}
                  title="PDF Viewer"
                />
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default ChecklistPdfButton;
